import {
  clearSessionProviderPin,
  pinSessionProvider,
  usesFirebaseSession
} from './session-config'

export const IDLE_KEY = 'blytzpay_session_activity'
export const LOGOUT_KEY = 'blytzpay_session_logout'
const TOKEN_KEYS = [
  'blytzpay_access_token',
  'blytzpay_refresh_token',
  'blytzpay_firebase_token'
]

export class SessionClosedError extends Error {
  constructor() {
    super('The session has ended')
    this.name = 'SessionClosedError'
    this.isSilent = true
  }
}

export function createSession({
  storage = localStorage,
  target = window,
  now = () => Date.now(),
  timeoutMs = Number(process.env.VUE_APP_SESSION_TIMEOUT) * 60000,
  idleMs = Number(process.env.VUE_APP_SESSION_IDLE) * 60000
} = {}) {
  let provider
  let user = null
  let principal = null
  let generation = 0
  let forced = null
  let pending = null
  let finishInitial
  let unsubscribe
  let hooks = {}
  let status = 'initializing'
  let authorization = null
  let claims = {}
  let deadline = null
  let closing = false
  let signedOutHere = false
  let expiryTimer
  let authorizationRevision = 0
  let subscriptionRevision = 0
  let profileBootstrap = null
  let profileGeneration = null
  let freshEntry = false

  function publish() {
    hooks.publish?.({ status, authorization, claims, principal, generation })
  }

  function assertCurrent(expected) {
    if (expected !== generation || closing) throw new SessionClosedError()
  }

  function readActivity() {
    try {
      const value = JSON.parse(storage.getItem(IDLE_KEY))

      return value?.principal === principal && Number.isFinite(value.deadline)
        ? value
        : null
    } catch {
      return null
    }
  }

  function checkIdle() {
    if (!user || closing) return false
    const activity = readActivity()
    if (!activity || activity.deadline <= now()) {
      void end({ reason: 'idle' })

      return false
    }
    deadline = activity.deadline
    clearTimeout(expiryTimer)
    expiryTimer = setTimeout(checkIdle, Math.max(1, deadline - now()))
    hooks.activity?.(
      deadline - timeoutMs,
      deadline - now() <= timeoutMs - idleMs
    )

    return true
  }

  function humanActivity() {
    if (!checkIdle()) return
    deadline = now() + timeoutMs
    storage.setItem(IDLE_KEY, JSON.stringify({ principal, deadline }))
    checkIdle()
  }

  async function token(forceRefresh = false) {
    if (!user || !checkIdle()) throw new SessionClosedError()
    const expected = generation
    const live = user
    if (forceRefresh && forced) return forced
    const request = (async () => {
      const value = await live.getIdToken(forceRefresh)
      assertCurrent(expected)
      if (!checkIdle()) throw new SessionClosedError()
      if (provider.currentUser() !== live) throw new SessionClosedError()

      return value
    })()
    if (!forceRefresh) return request
    forced = request
    try {
      return await request
    } finally {
      if (forced === request) forced = null
    }
  }

  function mePath() {
    return provider.kind === 'super'
      ? '/auth/v1/admin/me'
      : `/auth/v1/merchant/${encodeURIComponent(claims.merchantId[0])}/me`
  }

  async function refreshAuthorization(revision) {
    const expected = generation
    authorization = null
    status = 'authorizing'
    publish()
    try {
      const value = await hooks.readAuthorization(mePath(), await token())
      assertCurrent(expected)
      if (revision !== authorizationRevision) throw new SessionClosedError()
      if (
        !value?.user ||
        !Array.isArray(value.roles) ||
        !Array.isArray(value.groups) ||
        !value.permission_settings ||
        Object.values(value.permission_settings).some(
          v => typeof v !== 'string'
        )
      ) {
        throw new Error('Invalid session authorization response')
      }
      if (value.roles.length === 0) {
        const error = new Error('This user has no merchant hub role')
        error.response = { status: 403 }
        throw error
      }
      authorization = value
      publish()

      return value
    } catch (error) {
      if (
        expected === generation &&
        !closing &&
        revision === authorizationRevision
      ) {
        authorization = null
        status = 'error'
        publish()
      }
      throw error
    }
  }

  function ensureProfileBootstrap(expected) {
    if (profileGeneration !== expected || !profileBootstrap) {
      profileGeneration = expected
      const work = Promise.resolve().then(() => {
        assertCurrent(expected)

        return hooks.bootstrap?.(expected)
      })
      profileBootstrap = work
      void work.catch(() => {
        if (profileBootstrap === work) profileBootstrap = null
      })
    }

    return profileBootstrap
  }

  async function bootstrap(expected) {
    const revision = ++authorizationRevision
    try {
      await token()
      if (revision !== authorizationRevision) return
      await refreshAuthorization(revision)
      assertCurrent(expected)
      if (revision !== authorizationRevision) return
      status = 'bootstrapping'
      publish()
      await ensureProfileBootstrap(expected)
      assertCurrent(expected)
      if (revision !== authorizationRevision) return
      if (!checkIdle()) return
      status = 'ready'
      publish()
    } catch (error) {
      if (
        expected !== generation ||
        closing ||
        revision !== authorizationRevision
      )
        return
      if (
        error?.response?.status === 401 ||
        error?.code === 'session/invalid-principal'
      ) {
        await end({ reason: 'credentials' })
      } else {
        status = 'error'
        publish()
        hooks.error?.(error)
      }
    }
  }

  function validatePrincipal(nextUser, c) {
    const validMerchant =
      provider.kind === 'merchant' &&
      c.tenantType === 'merchant' &&
      Array.isArray(c.merchantId) &&
      c.merchantId.length === 1 &&
      /^[1-9][0-9]*$/.test(String(c.merchantId[0]))
    const validSuper = provider.kind === 'super' && c.tenantType === 'super'
    const roles = Array.isArray(c.roles) ? c.roles : [c.role]
    if (
      nextUser.tenantId !== provider.tenantId ||
      c.firebase?.tenant !== provider.tenantId ||
      (!validMerchant && !validSuper) ||
      c.clientId ||
      c.terminalSessionId ||
      roles.some(role =>
        ['consumer', 'machine', 'api_client', 'terminal'].includes(role)
      )
    ) {
      const error = new Error('This principal cannot enter the merchant hub')
      error.code = 'session/invalid-principal'
      throw error
    }
  }

  async function accept(nextUser) {
    if (closing || signedOutHere) return
    if (!nextUser) {
      if (user) await end({ reason: 'sdk' })
      else {
        signedOutHere = true
        freshEntry = false
        ++subscriptionRevision
        unsubscribe?.()
        unsubscribe = undefined
        status = 'signed-out'
        publish()
      }

      return
    }
    const nextPrincipal = `${provider.tenantId}:${nextUser.uid}`
    if (
      principal === nextPrincipal &&
      user === nextUser &&
      Object.keys(claims).length
    ) {
      const expected = generation
      try {
        const result = await nextUser.getIdTokenResult()
        assertCurrent(expected)
        validatePrincipal(nextUser, result.claims)
        if (JSON.stringify(result.claims) !== JSON.stringify(claims)) {
          const previous = {
            role: claims.role,
            roles: claims.roles,
            merchantId: claims.merchantId
          }
          const next = {
            role: result.claims.role,
            roles: result.claims.roles,
            merchantId: result.claims.merchantId
          }
          const scopeChanged =
            JSON.stringify(claims.merchantId) !==
            JSON.stringify(result.claims.merchantId)
          claims = result.claims
          if (scopeChanged) {
            ++generation
            forced = null
            authorization = null
            hooks.clear?.()
            publish()
          }
          if (JSON.stringify(previous) !== JSON.stringify(next))
            await bootstrap(scopeChanged ? generation : expected)
        }
      } catch (error) {
        if (
          expected === generation &&
          error.code === 'session/invalid-principal'
        )
          await end({ reason: 'credentials' })
      }

      return
    }
    const expected = ++generation
    forced = null
    authorization = null
    claims = {}
    status = 'identity'
    user = nextUser
    principal = nextPrincipal
    hooks.clear?.()
    publish()
    try {
      const result = await nextUser.getIdTokenResult()
      assertCurrent(expected)
      const c = result.claims
      validatePrincipal(nextUser, c)
      claims = c
      pinSessionProvider('firebase')
      TOKEN_KEYS.forEach(key => storage.removeItem(key))
      const activity = readActivity()
      const authenticatedDeadline = Number(c.auth_time) * 1000 + timeoutMs
      const isFreshEntry =
        freshEntry &&
        Number.isFinite(authenticatedDeadline) &&
        authenticatedDeadline > now()
      freshEntry = false
      if (!activity || isFreshEntry) {
        deadline = authenticatedDeadline
        if (!Number.isFinite(deadline)) deadline = 0
        storage.setItem(IDLE_KEY, JSON.stringify({ principal, deadline }))
      }
      if (!checkIdle()) return
      await bootstrap(expected)
    } catch (error) {
      if (expected !== generation || closing) return
      if (error?.code === 'session/invalid-principal') {
        hooks.error?.(error)
        await end({ reason: 'credentials' })
      } else {
        status = 'error'
        publish()
        hooks.error?.(error)
      }
    }
  }

  async function end({
    explicit = false,
    reason = 'local',
    broadcast = true
  } = {}) {
    if (closing) return
    closing = true
    ++subscriptionRevision
    freshEntry = false
    unsubscribe?.()
    unsubscribe = undefined
    clearTimeout(expiryTimer)
    signedOutHere = true
    const live = user
    const path =
      principal && (claims.merchantId || provider.kind === 'super')
        ? mePath()
        : null
    const oldPrincipal = principal
    ++generation
    user = null
    principal = null
    authorization = null
    claims = {}
    forced = null
    pending = null
    finishInitial?.()
    finishInitial = undefined
    status = 'signed-out'
    hooks.clear?.()
    publish()
    if (broadcast && oldPrincipal) {
      storage.setItem(
        LOGOUT_KEY,
        JSON.stringify({ principal: oldPrincipal, at: now(), generation })
      )
    }
    try {
      if (explicit && live && path) {
        const bearer = await live.getIdToken()
        await hooks.revoke?.(`${path}/logout`, bearer)
      }
    } catch (error) {
      hooks.revocationFailed?.(error)
    } finally {
      try {
        await provider?.signOut()
      } finally {
        TOKEN_KEYS.forEach(key => storage.removeItem(key))
        clearSessionProviderPin('firebase')
        hooks.clear?.()
        closing = false
        publish()
        hooks.signedOut?.(reason)
      }
    }
  }

  function onStorage(event) {
    if (event.key === LOGOUT_KEY && event.newValue) {
      try {
        if (JSON.parse(event.newValue).principal === principal) {
          void end({ reason: 'tab', broadcast: false })
        }
      } catch {
        /* Ignore malformed cross-tab notifications. */
      }
    } else if (event.key === IDLE_KEY || event.key === null) {
      checkIdle()
    }
  }

  return {
    configure(value) {
      hooks = value
    },
    start(value, options = {}) {
      if (pending) return pending
      unsubscribe?.()
      freshEntry = options.freshEntry === true
      if (freshEntry) {
        ++generation
        user = null
        principal = null
        claims = {}
        authorization = null
        forced = null
        status = 'initializing'
        hooks.clear?.()
        publish()
      }
      provider = value
      signedOutHere = false
      target.addEventListener('storage', onStorage)
      const subscription = ++subscriptionRevision
      pending = new Promise((resolve, reject) => {
        finishInitial = resolve
        let initial = true
        unsubscribe = provider.subscribe(next => {
          if (subscription !== subscriptionRevision) return Promise.resolve()
          const work = accept(next)
          if (initial) {
            initial = false
            work.then(resolve, reject)
          } else void work.catch(error => hooks.error?.(error))

          return work
        })
        if (subscription !== subscriptionRevision) {
          unsubscribe?.()
          unsubscribe = undefined
        }
      })

      return pending
    },
    retryBootstrap() {
      return Object.keys(claims).length
        ? bootstrap(generation)
        : accept(provider.currentUser())
    },
    refreshAuthorization: () => bootstrap(generation),
    getSessionIdToken: token,
    end,
    checkIdle,
    humanActivity,
    assertCurrent,
    get generation() {
      return generation
    },
    get ready() {
      return status === 'ready' && checkIdle()
    },
    get status() {
      return status
    },
    get isClosing() {
      return closing
    },
    dispose() {
      ++subscriptionRevision
      freshEntry = false
      ++generation
      pending = null
      finishInitial?.()
      finishInitial = undefined
      clearTimeout(expiryTimer)
      unsubscribe?.()
      unsubscribe = undefined
      target.removeEventListener('storage', onStorage)
    }
  }
}

export const session = createSession()

export async function getSessionIdToken(forceRefresh = false) {
  if (usesFirebaseSession()) return session.getSessionIdToken(forceRefresh)
  const { default: store } = await import('@/store')
  if (forceRefresh) await store.dispatch('refreshAccess')

  return store.state.authStore.accessToken
}
